import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Card from "../ui/Card";
import FormItem from "../ui/FormItem";


import { toast } from "react-toastify";

import { useAuthStore } from "../../store/authStore";

import FormActions from "../ui/FormActions";
import { validateEmail } from "../../utils/validation";
import { useLoginMutation } from "../../queries/auth/useLoginMutation";
import { EyeOffIcon, EyeIcon } from "lucide-react";

// A reusable login form that can be configured for agent/admin
export default function LoginForm({
  role = "agent",
  title = "Login",
  subtext = "",
}) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const location = useLocation();

  const pathRole = location.pathname.includes("admin") ? "admin" : "agent";

  let navLoginLink = "/admin-login";
  if (pathRole === "admin") {
    navLoginLink = "/";
  }

  let navLoginText = "Agent? Login here";
  if (pathRole !== "admin") {
    navLoginText = "Admin? Login here";
  }

  const [errors, setErrors] = useState({});

  const loginMutation = useLoginMutation(pathRole);

  const { setUser, setToken, setRole } = useAuthStore();


  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail(formData.email)) {
      setErrors({ email: "Invalid email address" });
      return;
    }

    if (!formData.password.trim()) {
      setErrors({ password: "Password is required" });
      return;
    }

    setErrors({});

    const formDataObj = new FormData();
    formDataObj.append("username", formData.email);
    formDataObj.append("password", formData.password);

    loginMutation.mutate(formDataObj, {
      onSuccess: (data) => {
        console.info("🚀 ~ handleSubmit ~ data:", data);
        setUser(data?.user);
        setRole(data?.user?.role)
        setToken(data?.access_token)
        toast.success("Login successful");
        // navigate to dashboard after successful login
        navigate("/dashboard");
      },
      onError: (error) => {
        console.error("🚀 ~ handleSubmit ~ error:", error);
        const errorMessage = error?.response?.data?.message || error?.detail || "Login failed. Please try again.";
        console.log("🚀 ~ handleSubmit ~ errorMessage:", errorMessage)
        toast.error(errorMessage);
      },
    });
  };

  const handleCancel = () => {
    setFormData({
      email: "",
      password: "",
    });
    setErrors({});
  };
  return (
    <>
      <Card className="login-card">
        <h2 className="login-title">{title}</h2>
        <div className="login-divider"></div>
        <div className="login-form-item">
          <form onSubmit={handleSubmit}>
            <FormItem
              label="Email Address"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              error={errors.email}
              required
            />
            <div className="password-field">
              <FormItem
                label="Password"
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                error={errors.password}
                required
              />
              <button
                type="button"
                className="password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowPassword((visible) => !visible)}
              >
                {showPassword ? (
                  <EyeOffIcon className="text-primary" size={18} />
                ) : (
                  <EyeIcon className="text-primary" size={18} />
                )}
              </button>
            </div>
            <FormActions
              loadingText="Logging in..."
              saveClass="login-button"
              isLoading={loginMutation.isPending}
              onCancel={handleCancel}
              saveText="Login"
            />
          </form>
          <div className="login-footer">
            <Link to={navLoginLink} className="admin-link">
              {navLoginText}
            </Link>
          </div>
        </div>
      </Card>
    </>
  );
}
